import { RecipientAddress } from '@proto/store/api/common/common';
import { Strings } from '@helper/strings';
import { AddressType } from '@helper/address-type';
import { Address, OverseaAddress, StoreAddress } from '@proto/api/common/common';

export class AddressErrorMessage {
  constructor(
    public type: AddressType | undefined = undefined,
    public key: keyof Address | keyof StoreAddress | keyof OverseaAddress | undefined = undefined,
    public message: string = ''
  ) {}
}

export class AddressHelper {
  static nameLength = 20;
  static phoneLength = 10;
  static minStreetLength = 4;
  static streetLength = 50;
  static overseaNameLength = 25;
  static overseaPhoneLength = 40;
  static overseaEmailLength = 100;
  static overseaStreetLength = 200;
  static overseaCityLength = 50;
  static overseaProvinceLength = 30;
  static overseaPostcodeLength = 15;

  static title(type: AddressType, isSelect: boolean, isCreate: boolean): string {
    if (isSelect) {
      if (type === AddressType.ADDRESS) {
        return $localize`選擇收貨地址`;
      }
      if (type === AddressType.STORE_ADDRESS) {
        return $localize`選擇超商取貨門市`;
      }
      if (type === AddressType.OVERSEA_ADDRESS) {
        return $localize`選擇海外收貨地址`;
      }
    } else if (isCreate) {
      if (type === AddressType.ADDRESS) {
        return $localize`新增收貨地址`;
      }
      if (type === AddressType.STORE_ADDRESS) {
        return $localize`新增超商取貨門市`;
      }
      if (type === AddressType.OVERSEA_ADDRESS) {
        return $localize`新增海外收貨地址`;
      }
    } else {
      if (type === AddressType.ADDRESS) {
        return $localize`編輯收貨地址`;
      }
      if (type === AddressType.STORE_ADDRESS) {
        return $localize`編輯超商取貨門市`;
      }
      if (type === AddressType.OVERSEA_ADDRESS) {
        return $localize`編輯海外收貨地址`;
      }
    }
    return '';
  }

  static needEmail(country: string): boolean {
    return country.includes('越南');
  }

  static needPostcode(country: string): boolean {
    return !country.includes('香港') && !country.includes('澳門');
  }

  static saveCheck(recipientAddress?: RecipientAddress, skipPhone?: boolean): AddressErrorMessage | undefined {
    const storeAddress = recipientAddress?.recipient?.storeAddress;
    const address = recipientAddress?.recipient?.address;
    const overseaAddress = recipientAddress?.recipient?.overseaAddress;
    if (!storeAddress && !address && !overseaAddress) {
      return new AddressErrorMessage();
    }
    if (storeAddress) {
      const name = storeAddress.name;
      if (!name) {
        return new AddressErrorMessage(AddressType.STORE_ADDRESS, 'name', $localize`請輸入姓名`);
      }
      if (Strings.includeNumber(name)) {
        return new AddressErrorMessage(AddressType.STORE_ADDRESS, 'name', $localize`姓名不能包含數字`);
      }
      if (!Strings.validStoreName(name)) {
        return new AddressErrorMessage(AddressType.STORE_ADDRESS, 'name', $localize`姓名至少為4個英文字或是2個中文字`);
      }
      if (!Strings.validStoreNameMax(name)) {
        return new AddressErrorMessage(AddressType.STORE_ADDRESS, 'name', $localize`姓名最多為10個英文字或是5個中文字`);
      }
      if (!Strings.validInternalName(name)) {
        return new AddressErrorMessage(AddressType.STORE_ADDRESS, 'name', $localize`姓名請勿輸入特殊符號`);
      }
      if (!skipPhone && !storeAddress.phone) {
        return new AddressErrorMessage(AddressType.STORE_ADDRESS, 'phone', $localize`請輸入手機號碼`);
      }
      if (!skipPhone && !Strings.validPhoneNumber(storeAddress.phone)) {
        return new AddressErrorMessage(AddressType.STORE_ADDRESS, 'phone', $localize`請輸入正確手機號碼`);
      }
      if (!storeAddress.storeId) {
        return new AddressErrorMessage(AddressType.STORE_ADDRESS, 'storeId', $localize`請選擇取件門市`);
      }
    }
    if (address) {
      if (!address.name) {
        return new AddressErrorMessage(AddressType.ADDRESS, 'name', $localize`請輸入姓名`);
      }
      if (Strings.includeNumber(address.name)) {
        return new AddressErrorMessage(AddressType.ADDRESS, 'name', $localize`姓名不能包含數字`);
      }
      if (!Strings.validInternalName(address.name)) {
        return new AddressErrorMessage(AddressType.ADDRESS, 'name', $localize`姓名請勿輸入特殊符號`);
      }
      if (!skipPhone && !address.phone) {
        return new AddressErrorMessage(AddressType.ADDRESS, 'phone', $localize`請輸入手機號碼`);
      }
      if (!skipPhone && !Strings.validPhoneNumber(address.phone)) {
        return new AddressErrorMessage(AddressType.ADDRESS, 'phone', $localize`請輸入正確手機號碼`);
      }
      if (!address.city || !address.area) {
        return new AddressErrorMessage(AddressType.ADDRESS, 'city', $localize`請選擇縣市/地區`);
      }
      if (!address.street) {
        return new AddressErrorMessage(AddressType.ADDRESS, 'street', $localize`請輸入收件地址`);
      }
      if (address.street.length < AddressHelper.minStreetLength) {
        return new AddressErrorMessage(AddressType.ADDRESS, 'street', $localize`請輸入正確收件地址`);
      }
    }
    if (overseaAddress) {
      if (!overseaAddress.country) {
        return new AddressErrorMessage(AddressType.OVERSEA_ADDRESS, 'country', $localize`請選擇國家/區域`);
      }
      if (!overseaAddress.firstName) {
        return new AddressErrorMessage(AddressType.OVERSEA_ADDRESS, 'firstName', $localize`請輸入名字`);
      }
      if (!overseaAddress.lastName) {
        return new AddressErrorMessage(AddressType.OVERSEA_ADDRESS, 'lastName', $localize`請輸入姓氏`);
      }
      if (!skipPhone && !overseaAddress.phone) {
        return new AddressErrorMessage(AddressType.OVERSEA_ADDRESS, 'phone', $localize`請輸入手機號碼`);
      }
      if (!skipPhone && !Strings.validOverseaPhoneNumber(overseaAddress.phone)) {
        return new AddressErrorMessage(AddressType.OVERSEA_ADDRESS, 'phone', $localize`請輸入正確手機號碼`);
      }
      if (AddressHelper.needEmail(overseaAddress.country)) {
        if (!overseaAddress.email) {
          return new AddressErrorMessage(AddressType.OVERSEA_ADDRESS, 'email', $localize`請輸入Email`);
        }
        if (!Strings.validEmail(overseaAddress.email)) {
          return new AddressErrorMessage(AddressType.OVERSEA_ADDRESS, 'email', $localize`請輸入正確Email`);
        }
      }
      if (!overseaAddress.street) {
        return new AddressErrorMessage(AddressType.OVERSEA_ADDRESS, 'street', $localize`請輸入收件地址`);
      }
      if (!overseaAddress.city) {
        return new AddressErrorMessage(AddressType.OVERSEA_ADDRESS, 'city', $localize`請輸入城市`);
      }
      if (!overseaAddress.province) {
        return new AddressErrorMessage(AddressType.OVERSEA_ADDRESS, 'province', $localize`請輸入州/省/地區`);
      }
      if (AddressHelper.needPostcode(overseaAddress.country)) {
        if (!overseaAddress.postcode) {
          return new AddressErrorMessage(AddressType.OVERSEA_ADDRESS, 'postcode', $localize`請輸入郵遞區號`);
        }
        if (!Strings.validPostcode(overseaAddress.postcode)) {
          return new AddressErrorMessage(AddressType.OVERSEA_ADDRESS, 'postcode', $localize`請輸入正確郵遞區號`);
        }
        if (!Strings.validOverseaString(overseaAddress.firstName)) {
          return new AddressErrorMessage(AddressType.OVERSEA_ADDRESS, 'firstName', $localize`姓名請輸入英文`);
        }
        if (!Strings.validOverseaString(overseaAddress.lastName)) {
          return new AddressErrorMessage(AddressType.OVERSEA_ADDRESS, 'lastName', $localize`姓氏請輸入英文`);
        }
        if (!Strings.validOverseaString(overseaAddress.street)) {
          return new AddressErrorMessage(AddressType.OVERSEA_ADDRESS, 'street', $localize`地址請輸入英文`);
        }
        if (!Strings.validOverseaString(overseaAddress.city)) {
          return new AddressErrorMessage(AddressType.OVERSEA_ADDRESS, 'city', $localize`城市請輸入英文`);
        }
        if (!Strings.validOverseaString(overseaAddress.province)) {
          return new AddressErrorMessage(AddressType.OVERSEA_ADDRESS, 'province', $localize`地區請輸入英文`);
        }
      }
    }
    return undefined;
  }
}
